import { useContext } from "react";

import {
  dataAttribute,
  DataAttributes,
  PivotItem,
  PivotTabs
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { useClaimAdjustmentContext } from "@modules/acc/screens/claim-adjustment/context/ClaimAdjustmentContext.ts";
import { ClaimPivotLinkKeys } from "@modules/acc/screens/claim/components/ClaimFormEnums.ts";
import { ClaimContext } from "@modules/acc/screens/claim/context/ClaimContext.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { DischargeContext } from "../claim-discharge/context/DischargeContext.ts";
import { AccBreadcrumbs } from "../claim-management/components/AccBreadcrumbs.tsx";
import { ClaimPages } from "../claim/types/claim.constants.ts";

export const ClaimPivotTabs: React.FC = () => {
  const { routing, core } = useStores();
  const claimContext = useContext(ClaimContext);
  const claimAdjustmentContext = useClaimAdjustmentContext();
  const dischargeContext = useContext(DischargeContext);

  const claim =
    claimContext?.claim ||
    claimAdjustmentContext.claim ||
    dischargeContext?.claim;

  const referred = claim.referralIn;
  const isImported = claim.isImported;
  const hideAcc45Tab = referred && !isImported;

  const getCurrentPage = () => {
    if (!!routes.claimAdjustment.edit.match(routing.location.pathname)) {
      return ClaimPages.claimAdjustmentEdit;
    } else if (!!routes.claims.edit.match(routing.location.pathname)) {
      return ClaimPages.claimEdit;
    } else if (
      !!routes.claims.discharge.edit.match(routing.location.pathname)
    ) {
      return ClaimPages.dischargeEdit;
    } else {
      return ClaimPages.managementEdit;
    }
  };

  const currentPage = getCurrentPage();

  const getSelectedKey = () => {
    switch (currentPage) {
      case ClaimPages.claimEdit:
        return ClaimPivotLinkKeys.acc45;
      case ClaimPages.dischargeEdit:
        return ClaimPivotLinkKeys.discharge;
      default:
        return ClaimPivotLinkKeys.management;
    }
  };

  const selectedKey = getSelectedKey();

  const handlePivotClicked = (item: PivotItem): void => {
    const selectedValue = item.props && item.props.itemKey;
    if (
      selectedValue === selectedKey &&
      currentPage !== ClaimPages.claimAdjustmentEdit
    ) {
      return;
    }

    switch (selectedValue) {
      case ClaimPivotLinkKeys.management: {
        routing.pushRetainingState(
          routes.claims.management.edit.path({ id: claim.id })
        );
        break;
      }
      case ClaimPivotLinkKeys.acc45: {
        routing.pushRetainingState(routes.claims.edit.path({ id: claim.id }));
        break;
      }
      case ClaimPivotLinkKeys.discharge: {
        routing.pushRetainingState(
          routes.claims.discharge.edit.path({ id: claim.id })
        );
        break;
      }
    }
  };

  return (
    <>
      <AccBreadcrumbs claim={claim} />
      <PivotTabs selectedKey={selectedKey} onLinkClick={handlePivotClicked}>
        <PivotItem
          {...dataAttribute(DataAttributes.Element, "management-button")}
          headerText="Summary"
          itemKey={ClaimPivotLinkKeys.management}
        />
        {!hideAcc45Tab && (
          <PivotItem
            {...dataAttribute(DataAttributes.Element, "acc45-button")}
            headerText="ACC45"
            itemKey={ClaimPivotLinkKeys.acc45}
          />
        )}

        {!claim.discharged &&
          core.hasPermissions(
            [
              Permission.ConditionDischargeOnBehalfAllowed,
              Permission.ClaimListDischargeAllowed
            ],
            "and"
          ) && (
            <PivotItem
              {...dataAttribute(DataAttributes.Element, "discharge-button")}
              headerText="Discharge"
              itemKey={ClaimPivotLinkKeys.discharge}
            />
          )}
      </PivotTabs>
    </>
  );
};
